import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormGroup,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validators
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigurationService, CoursesService, UserManagementService } from '@draeger-academy/common';
import { AuthenticationService } from '@draeger-academy/common/lib/services/authentication/authentication.service';
import { OrderService } from '@draeger-academy/common/lib/services/order/order.service';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { Order } from 'models/order';
import { Person } from 'models/person';
import { User } from 'models/user';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { first, take } from 'rxjs/operators';
import { FormsUtils } from '../form-builder';
import { Page } from 'ngx-pagination/lib/pagination-controls.directive';

@Component({
    selector: 'da-person-form',
    templateUrl: './person-form.component.html',
    styleUrls: ['./person-form.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PersonFormComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => PersonFormComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PersonFormComponent implements OnInit, ControlValueAccessor, OnDestroy {
    // public personalForm: FormGroup;
    loggedIn = false;
    loading = false;
    submitted = false;
    hasFocusZipCode = false;
    hasFocusTitle = false;
    hasFocusDepartment = false;
    hasFocusPhone = false;
    hasFocusCompany = false;
    hasFocusAddress = false;
    hasFocusHouseNumber = false;
    isEditMode = false;
    hasFocusAddressAdditional = false;
    hasFocusCity = false;
    hasFocusCountry = false;
    hasFocusRegion = false;
    hasFocusSalutation = false;
    hasFocusFirstName = false;
    hasFocusLastName = false;
    hasFocusEmail = false;
    showError = false;
    user: User = new User([]);
    @Input() personalForm: UntypedFormGroup;
    @Input() hideSameAsCompany = false;
    @Input() index: number;
    @Input() events: Observable<void>;
    @Input() isBookingPerson = true;
    @Input() isBillingContact: boolean;
    @Input() bookingForm: UntypedFormGroup;
    @Input() formTitle: string;
    // tslint:disable-next-line:no-input-rename
    @Input('children') public children: UntypedFormArray;
    @Input() person: any;
    @Input() bookingPerson: Person;
    @Input() stepNum: number;
    private eventsSubscription: Subscription;
    private _subscription: Subscription;
    public showBackButton = false;
    private _currentData = null;
    public selectedCountry = '';
    public selectedRegion = '';
    public selectedSalutation = '';
    public salutationsData: any;
    public countriesData: any;
    public preselectedCountry: string;

    @Output()
    inputChanged: EventEmitter<number> = new EventEmitter<number>();
    @Output()
    preselectCountryCode: EventEmitter<string> = new EventEmitter<string>();

    @Output()
    sameAs: EventEmitter<boolean> = new EventEmitter<boolean>();

    private _$user: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public get user$(): Observable<boolean> {
        return this._$user.asObservable();
    }

    public _$showChangePassword: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public get showChangePassword$(): Observable<boolean> {
        return this._$showChangePassword.asObservable();
    }

    private _$showError: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public get showError$(): Observable<{}> {
        return this._$showError.asObservable();
    }

    private _$countries: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    public get countries$(): Observable<{}> {
        return this._$countries.asObservable();
    }

    private _$regions: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    public get regions$(): Observable<{}> {
        return this._$regions.asObservable();
    }

    private _$salutations: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    public get salutations$(): Observable<{}> {
        return this._$salutations.asObservable();
    }

    private _$order: BehaviorSubject<any> = new BehaviorSubject([]);
    public get order$(): Observable<Order> {
        return this._$order.asObservable();
    }

    private _$hasRegions: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public get hasRegions$(): Observable<boolean> {
        return this._$hasRegions.asObservable();
    }

    editTitle = true;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private authenticationService: AuthenticationService,
        private _localizeRouterService: LocalizeRouterService,
        private userService: UserManagementService,
        private el: ElementRef,
        private usermanagement: UserManagementService,
        private _configurationService: ConfigurationService,
        private _orderService: OrderService,
        private _coursesService: CoursesService
    ) {}

    ngOnInit() {
        this._coursesService.languageSelected.subscribe((language) => {
            this.salutationsData = this._configurationService.$allSalutationsByLanguage.getValue();
            this.countriesData = this._configurationService.$allCountriesByLanguage.getValue();
            this.orderCountries(this.countriesData);
            this.preselectCountry();
            this.setForm();
        });
        this.salutationsData = this._configurationService.$allSalutationsByLanguage.getValue();
        this.countriesData = this._configurationService.$allCountriesByLanguage.getValue();

        this.orderCountries(this.countriesData);
        this.preselectCountry();

        if (this.authenticationService.isLoggedIn()) {
            this.loggedIn = true;
        } else {
            this.loggedIn = false;
        }
        this.personalForm = this.formBuilder.group({
            title: ['', Validators.maxLength(40)],
            lastName: ['', [Validators.required, Validators.maxLength(35)]],
            firstName: ['', [Validators.required, Validators.maxLength(50)]],
            department: ['', Validators.maxLength(50)],
            address: ['', [Validators.required, Validators.maxLength(44)]],
            houseNumber: ['', Validators.maxLength(5)],
            phone: [''],
            zipCode: ['', [Validators.required, Validators.maxLength(10)]],
            city: ['', [Validators.required, Validators.maxLength(40)]],
            country: [null, [Validators.required, Validators.maxLength(20)]],
            regionKey: [null],
            company: [null, [Validators.required, Validators.maxLength(50)]],
            subscribe: false,
            salutation: [null],
            email: ['', [Validators.required, Validators.email, Validators.maxLength(100)]],
            companySameAs: false
        });

        if (this.events) {
            this.eventsSubscription = this.events.subscribe(() => (this.submitted = true));
        }

        this.setForm();

        this._subscription = this.route.queryParams.subscribe((params) => {
            this.showBackButton = Boolean(params['booking']) ? true : false;
        });

        this._orderService.order$.subscribe((order) => {
            this._$order.next(order);
        });
    }

    get f() {
        return this.personalForm.controls;
    }

    public hasValue(fieldname) {
        const _input = fieldname;
        return _input.value ? FormsUtils.trim(_input.value.toString()) !== '' : false;
    }

    onSubmit() {
        this.submitted = true;
        this.user.title = this.f.title.value;
        this.user.company.department = this.f.department.value;
        this.user.company.street = this.f.address.value;
        this.user.company.houseNumber = this.f.houseNumber.value;
        this.user.company.phone = this.f.phone.value;
        this.user.company.zipCode = this.f.zipCode.value;
        this.user.company.city = this.f.city.value;
        this.user.company.country = this.f.country.value;
        this.user.company.regionKey = this.f.regionKey.value;
        this.user.salutation = this.f.salutation.value;
        this.user.subscribe = this.f.subscribe.value;
        this.user.phoneNumber = this.f.phone.value;

        this.loading = true;
        this.usermanagement.updateUser(this.user).then(
            (data) => {
                this.loading = false;
                // window.location.href = this.returnUrl;
                // TO DO: header is not updating on the redirect - and login link is not hidden once we log in until we refresh the page
            },
            (error) => {
                console.log(error);
                this._$showError.next(true);
                this.loading = false;
            }
        );
    }

    resetForm() {
        this._$user.subscribe((data) => {
            const user = new User(data);
            if (user) {
                this.f.title.setValue(user.title);
                this.f.department.setValue(user.company.department);
                this.f.address.setValue(user.company.street);
                this.f.houseNumber.setValue(user.company.houseNumber);
                this.f.zipCode.setValue(user.company.zipCode);
                this.f.city.setValue(user.company.city);
                this.f.country.setValue(user.company.country.code);
                this.f.regionKey.setValue(user.company.regionKey);
                this.f.salutation.setValue(user.salutation.code);
            }
        });
    }

    public onTouched: () => void = () => {};

    writeValue(val: any): void {
        // tslint:disable-next-line:no-unused-expression
        val && this.personalForm.patchValue(val, { emitEvent: false });
    }
    registerOnChange(fn: any): void {
        this.personalForm.valueChanges.subscribe(fn);
    }
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }
    setDisabledState?(isDisabled: boolean): void {
        isDisabled ? this.personalForm.disable() : this.personalForm.enable();
    }

    validate(c: AbstractControl): ValidationErrors | null {
        return this.personalForm.valid ? null : { invalidForm: { valid: false, message: 'Form is invalid' } };
    }

    ngOnDestroy() {
        this._subscription.unsubscribe();
        if (this.eventsSubscription) {
            this.eventsSubscription.unsubscribe();
        }
    }

    setForm() {
        this.authenticationService.loggedIn().subscribe((data) => {
            if (data) {
                this.userService.getUser().then((user) => {
                    this.getConfigDatafromPartalsandWebsites(user);
                });
            } else {
                const user = null;
                this.getConfigDatafromPartalsandWebsites(user);
            }
        });
    }

    private getConfigDatafromPartalsandWebsites(user) {
        if (!this._configurationService.isPartnerPage()) {
            this._configurationService.$siteSettings.subscribe((data) => {
                if (data) {
                    this.setFormCountriesSalutationRegion(user, data);
                }
            });
        } else {
            this._configurationService.getConfigPartnerByPortal().then((values) => {
                if (values) {
                    this.setFormCountriesSalutationRegion(user, values);
                }
            });
        }
    }

    setFormCountriesSalutationRegion(user, data) {
        const _regionsData = data.filter((f) => f?.key.toLowerCase() === 'regions')[0];
        this._currentData = data;

        if (this.salutationsData) {
            this._$salutations.next(this.salutationsData);
        }
        if (this.countriesData) {
            this._$countries.next(this.countriesData);
        }
        if (_regionsData && _regionsData['value']) {
            this._$regions.next(_regionsData['value']);
            this._$hasRegions.next(true);
        } else {
            this._$regions.next(null);
            this.f.regionKey.setValue(null);
            this.selectedRegion = null;
            this._$hasRegions.next(false);
        }

        if (user && this.stepNum === 1) {
            if (user.salutation && user.salutation.code !== null && user.salutation.code !== '' && this.index === 0) {
                this.f.salutation.setValue(user.salutation.code);
                this.selectedSalutation = user.salutation.code;
            } else if (
                user.billingContact.salutation &&
                user.billingContact.salutation.code !== null &&
                user.billingContact.salutation.code !== '' &&
                this.index === 1
            ) {
                this.f.salutation.setValue(user.billingContact.salutation.code);
                this.selectedSalutation = user.billingContact.salutation.code;
            } else {
                this.selectedSalutation = this.salutationsData[0].key;
                this.f.salutation.setValue(this.salutationsData[0].key);
            }
            if (user.country && user.country.code !== null && user.country.code !== '' && this.index === 0) {
                this.f.country.setValue(user.country.code);
                this.selectedCountry = user.country.code;
            } else if (
                user.billingContact.country &&
                user.billingContact.country.code !== null &&
                user.billingContact.country.code !== '' &&
                this.index === 1
            ) {
                this.f.country.setValue(user.billingContact.country.code);
                this.selectedCountry = user.billingContact.country.code;
            } else {
                this.selectedCountry = this.preselectedCountry;
                this.f.country.setValue(this.preselectedCountry);
            }
            if (user.regionKey && user.regionKey !== null && user.regionKey !== '' && this.index === 0) {
                this.f.regionKey.setValue(user.regionKey);
                this.selectedRegion = user.regionKey;
            } else if (
                user.billingContact.regionKey &&
                user.billingContact.regionKey !== null &&
                user.billingContact.regionKey !== '' &&
                this.index === 1
            ) {
                this.f.regionKey.setValue(user.billingContact.regionKey);
                this.selectedRegion = user.billingContact.regionKey;
            } else {
                if (_regionsData) {
                    this.selectedRegion = _regionsData['value'][0];
                    this.f.regionKey.setValue(_regionsData['value'][0]);
                }
            }
        } else {
            this.setSalutationCountryRegionEmpty(_regionsData);
        }
        this._orderService.order$.subscribe((order) => {
            if (order) {
                if (this.stepNum === 1) {
                    if (order.user && order.user !== null && (this.index === 0 || !this.index)) {
                        this.selectedSalutation = order.user.salutation;
                        this.f.salutation.setValue(order.user.salutation);
                        this.selectedCountry = order.user.country;
                        this.f.country.setValue(order.user.country);
                        this.selectedRegion = order.user.regionKey;
                        this.f.regionKey.setValue(order.user.regionKey);
                    } else if (order.billing && order.billing !== null && this.index === 1) {
                        this.f.salutation.setValue(order.billing.salutation);
                        this.selectedSalutation = order.billing.salutation;
                        this.selectedCountry = order.billing.country;
                        this.f.country.setValue(order.billing.country);
                        this.selectedRegion = order.billing.regionKey;
                        this.f.regionKey.setValue(order.billing.regionKey);
                    }
                } else if (this.stepNum === 2) {
                    if (order.participants && order.participants !== null && order.participants.length > 0) {
                        if (order.participants[this.index].person) {
                            if (order.participants[this.index].person.salutation !== null) {
                                setTimeout(() => {
                                    this.selectedSalutation = order.participants[this.index].person.salutation;
                                    this.f.salutation.setValue(order.participants[this.index].person.salutation);
                                });
                                this.selectedCountry = order.participants[this.index].person.country;
                                this.f.country.setValue(order.participants[this.index].person.country);
                                this.selectedRegion = order.participants[this.index].person.regionKey;
                                this.f.regionKey.setValue(order.participants[this.index].person.regionKey);
                            }
                        }
                    }
                }
                this._$order.next(order);
            }
        });
        this.user = user;
        this._$user.next(user);
    }

    public setSalutationCountryRegionEmpty(_regionsData) {
        this.selectedSalutation = this.salutationsData[0]?.key;
        this.f.salutation.setValue(this.salutationsData[0]?.key);
        this.selectedCountry = this.preselectedCountry;
        this.f.country.setValue(this.preselectedCountry);
        if (_regionsData) {
            this.selectedRegion = _regionsData['value'][0];
            this.f.regionKey.setValue(_regionsData['value'][0]);
        }
    }

    public companySameAs(event) {
        let persons = null;
        if (this.bookingForm) {
            persons = this.bookingForm.get('persons') as UntypedFormArray;
        }
        if (persons) {
            if (event) {
                const firstParticipant = persons.controls[0].get('person').value as Person;
                this.f.salutation.setValue(firstParticipant.salutation);
                this.f.country.setValue(firstParticipant.country);
                this.f.regionKey.setValue(firstParticipant.regionKey);
                this.f.address.setValue(firstParticipant.address);
                this.f.houseNumber.setValue(firstParticipant.houseNumber);
                this.f.company.setValue(firstParticipant.company);
                this.f.zipCode.setValue(firstParticipant.zipCode);
                this.f.city.setValue(firstParticipant.city);
            } else {
                this.setCompanyFieldsEmpty();
            }
        }
    }

    setCompanyFieldsEmpty() {
        this._configurationService.getConfigPartnerByPortal().then((values) => {
            if (values) {
                const _regionsData = values.filter((f) => f.key.toLowerCase() === 'regions')[0];
                let region;
                if (_regionsData && _regionsData['value']) {
                    region = _regionsData['value'][0];
                } else {
                    region = '';
                }
                this.f.address.setValue('');
                this.f.company.setValue('');
                this.f.zipCode.setValue('');
                this.f.city.setValue('');
                this.f.regionKey.setValue(region);
                this.f.country.setValue(this.preselectedCountry);
            }
        });
    }

    public onKey(event) {
        if (this.index !== 0) {
            this.f.companySameAs.setValue(false);
        }
    }

    @HostListener('keyup', ['$event']) public onKeyup(event: KeyboardEvent): void {
        if (this.index === 0) {
            this.inputChanged.emit(this.index);
        }
    }

    @HostListener('change', ['$event']) public change(event): void {
        if (this.index === 0) {
            this.inputChanged.emit(this.index);
        } else {
            this._configurationService.updateIfBillingContactEdited(true);
        }
        if (event.target.type === 'checkbox') {
            this.sameAs.emit(this.f.companySameAs.value);
        }
    }

    private orderCountries(countries) {
        countries.sort((a, b) => {
            if (a.value > b.value) {
                return 1;
            } else if (a.value < b.value) {
                return -1;
            } else {
                return 0;
            }
        });
        this._$countries.next(countries);
    }

    private preselectCountry() {
        const isPortal = this._configurationService.isPartnerPage();
        if (!isPortal) {
            const _site = this._configurationService.getCurrentSite();
            if (_site) {
                const _country = _site.split('_')[1].toLocaleUpperCase();
                this.preselectedCountry = _country;
            } else {
                this.preselectedCountry = 'DE';
            }
        } else {
            const countryCode = JSON.parse(localStorage.getItem('portalCountryCode')).countryCode;
            this.preselectedCountry = countryCode.split('_')[1].toLocaleUpperCase();
            this.preselectCountryCode.emit(this.preselectedCountry);
        }
    }
}
